/**
 * Centralized query key factory for React Query.
 *
 * Follows the factory pattern recommended by TkDodo:
 * https://tkdodo.eu/blog/effective-react-query-keys#use-query-key-factories
 *
 * Usage:
 *   import { queryKeys } from "@/lib/query-keys";
 *   useQuery({ queryKey: queryKeys.trips.all, ... })
 *   useQuery({ queryKey: queryKeys.trips.detail(tripId), ... })
 *   queryClient.invalidateQueries({ queryKey: queryKeys.trips.all })
 */

export const queryKeys = {
  // --- Auth ---
  auth: {
    user: ["/api/auth/user"] as const,
  },

  // --- Trips ---
  trips: {
    all: ["/api/trips"] as const,
    mine: ["/api/trips/mine"] as const,
    detail: (tripId: string) => ["/api/trips", tripId] as const,
    expenses: (tripId: string) => ["/api/trips", tripId, "expenses"] as const,
    settlements: (tripId: string) => ["/api/trips", tripId, "settlements"] as const,
    participants: (tripId: string) =>
      ["/api/trips", tripId, "participants"] as const,
    budgetValidations: (tripId: string) =>
      ["/api/trips", tripId, "budget-validations"] as const,
    challenges: (tripId: string) =>
      ["/api/trips", tripId, "challenges"] as const,
    challengeLeaderboard: (tripId: string) =>
      ["/api/trips", tripId, "challenges", "leaderboard"] as const,
    challengeCompletions: (tripId: string) =>
      ["/api/trips", tripId, "challenges", "completions"] as const,
    logistics: (tripId: string) =>
      ["/api/trips", tripId, "logistics"] as const,
    bookingStrategies: (tripId: string) =>
      ["/api/trips", tripId, "booking-strategies"] as const,
    photos: (tripId: string) => ["/api/trips", tripId, "photos"] as const,
    troopSizeProposals: (tripId: string) =>
      ["/api/trips", tripId, "troop-size-proposals"] as const,
    resourcePledges: (tripId: string) =>
      ["/api/trips", tripId, "resource-pledges"] as const,
    itineraryProposals: (tripId: string) =>
      ["/api/trips", tripId, "itinerary", "proposals"] as const,
  },

  // --- Users ---
  users: {
    all: ["/api/users"] as const,
    detail: (userId: string) => ["/api/users", userId] as const,
  },

  // --- Wishlist ---
  wishlist: {
    all: ["/api/wishlist"] as const,
    user: (userId: string) => ["/api/wishlist/user", userId] as const,
  },

  // --- Stage 0 (AI Planner data lookup) ---
  stageZero: {
    lookup: (destination: string, startDate: string, endDate: string) =>
      ["/api/trips/stage0", destination, startDate, endDate] as const,
  },

  // --- Usage ---
  usage: {
    all: ["/api/usage"] as const,
  },

  // --- Notifications ---
  notifications: {
    all: ["/api/notifications"] as const,
    unreadCount: ["/api/notifications/unread-count"] as const,
  },

  // --- Connections ---
  connections: {
    byUser: (userId: string) => ["/api/connections", userId] as const,
  },

  // --- Friends ---
  friends: {
    byUser: (userId: string) => ["/api/friends", userId] as const,
  },

  // --- Favorites ---
  favorites: {
    leaderboard: ["/api/favorites/leaderboard"] as const,
    active: (userId: string) => ["/api/favorites/active", userId] as const,
    remaining: (userId: string) =>
      ["/api/favorites/remaining", userId] as const,
    count: (userId: string) =>
      [`/api/favorites/count/${userId}`] as const,
  },

  // --- Resources ---
  resources: {
    all: ["/api/resources"] as const,
  },

  // --- Community ---
  community: {
    posts: ["/api/community-posts"] as const,
  },

  // --- Travel Calendar ---
  travelCalendar: {
    all: ["/api/travel-calendar"] as const,
    byYear: (year: string) => ["/api/travel-calendar", year] as const,
  },

  // --- Photos ---
  photos: {
    all: ["/api/photos/all"] as const,
  },

  // --- Subscription ---
  subscription: {
    gracePeriod: ["/api/subscription/grace-period"] as const,
  },

  // --- Roles ---
  roles: {
    all: ["/api/roles"] as const,
  },

  // --- Admin ---
  admin: {
    reports: ["/api/admin/reports"] as const,
  },

  // --- Milestones ---
  milestones: {
    byUser: (userId: string) => ["milestones", userId] as const,
  },

  // --- Social Fabric ---
  social: {
    allies: ["/api/social/allies"] as const,
    leaderboard: (dimension: string, scope: string, category?: string) =>
      ["/api/social/leaderboard", dimension, scope, ...(category ? [category] : [])] as const,
    progression: (userId: string) =>
      ["/api/social/progression", userId] as const,
    connection: (userId: string) =>
      ["/api/social/connection", userId] as const,
    warmIntroPending: ["/api/social/warm-intro/pending"] as const,
    nudges: ["/api/social/nudges"] as const,
  },

  // --- Features ---
  features: {
    visibility: ["/api/features/visibility"] as const,
  },
} as const;
